import { FC, ReactNode } from 'react'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import Carousel from 'react-multi-carousel'

import type {
  LegacyCoinLocker,
  LegacyCoinLockerPhoto,
} from 'features/coin-locker/types/legacyCoinLocker'

import styles from './RecommendedCoinLockerCard.module.scss'

interface Props {
  coinLocker: LegacyCoinLocker
  locale: string
}

const RecommendedCoinLockerCardOld: FC<Props> = ({ coinLocker }): JSX.Element => {
  const { t } = useTranslation()
  const lockerPlaceUrl = coinLocker.locker_place_url

  const carouselResponsive = {
    desktop: {
      breakpoint: { max: 3000, min: 992 },
      items: 1,
    },
    tablet: {
      breakpoint: { max: 991, min: 576 },
      items: 1,
    },
    mobile: {
      breakpoint: { max: 575, min: 0 },
      items: 1,
    },
  }

  const wrapWithLink = (
    children: ReactNode,
    className: string,
    options?: { target?: string }
  ): JSX.Element => {
    if (!lockerPlaceUrl) {
      return <div className={className}>{children}</div>
    }

    return (
      <Link href={lockerPlaceUrl}>
        <a className={className} target={options?.target}>
          {children}
        </a>
      </Link>
    )
  }

  return (
    <div className={styles.coinLockerCard}>
      {coinLocker.locker_photos.length > 0 ? (
        <div className={styles.coinLockerCardPhotoSection}>
          <Carousel
            ssr
            responsive={carouselResponsive}
            infinite
            swipeable={false}
            arrows={true}
            className={styles.coinLockerCardPhotoList}
            customLeftArrow={
              <button
                className={`${styles.coinLockerCardPhotoListArrow} ${styles.coinLockerCardPhotoListArrowLeft}`}
              />
            }
            customRightArrow={
              <button
                className={`${styles.coinLockerCardPhotoListArrow} ${styles.coinLockerCardPhotoListArrowRight}`}
              />
            }
          >
            {coinLocker.locker_photos.map((photo: LegacyCoinLockerPhoto, index) => {
              return (
                <div key={index} className={styles.coinLockerCardPhotoListItem}>
                  <img
                    className={styles.coinLockerCardPhotoImage}
                    src={photo.url ?? '/location_popular_area_default.webp'}
                    alt={coinLocker.name}
                  />
                </div>
              )
            })}
          </Carousel>
        </div>
      ) : (
        <div className={styles.coinLockerCardPhotoSection}>
          <div className={styles.coinLockerCardPhotoListItem}>
            <img
              className={styles.coinLockerCardPhotoImage}
              src="/location_popular_area_default.webp"
              alt=""
            />
          </div>
        </div>
      )}
      <div className={styles.coinLockerCardContent}>
        {wrapWithLink(
          <>
            <h3 className={styles.coinLockerCardTitle}>{coinLocker.name}</h3>
            <div className={styles.coinLockerCardDistanceTime}>
              <span className={styles.coinLockerCardDistanceTimeText}>
                <img
                  className={styles.coinLockerCardDistanceTimeImage}
                  src="/map_pin_icon_gray.svg"
                  alt=""
                />
                {t('COMMON.HOW_MANY_MINUTES_WALK_FROM_THE_STATION', {
                  stationName: coinLocker.nearest_station,
                  minutes: coinLocker.walking_time,
                })}
              </span>
              <span className={styles.coinLockerCardDistanceTimeText}>
                <img
                  className={styles.coinLockerCardDistanceTimeImage}
                  src="/business_hours_icon_gray.svg"
                  alt=""
                />
                {t('COMMON.TODAY_BUSINESS_TIME')} : {coinLocker.open_at_today}〜
                {coinLocker.close_at_today}
              </span>
            </div>
            <div className={styles.coinLockerCardNote}>
              <div className={styles.coinLockerCardNoteComment}>
                <p className={styles.coinLockerCardNoteCommentText}>{coinLocker.extra_info}</p>
              </div>
            </div>
          </>,
          styles.coinLockerCardContentLink
        )}
      </div>
      <div className={styles.coinLockerCardPriceSize}>
        {wrapWithLink(
          <dl className={styles.coinLockerCardPriceSizeBox}>
            <dt className={styles.coinLockerCardPriceSizeTitle}>
              {t('COMMON.CAN_STORE_LUGGAGE_COUNT')}
            </dt>
            <dd className={styles.coinLockerCardPriceSizeDescription}>
              <div className={styles.coinLockerCardPriceSizeNumberPrice}>
                {coinLocker.storage?.large_locker_storage && (
                  <span className={styles.coinLockerCardPriceSizeNumberPriceItem}>
                    {t('COMMON.COIN_LOCKER.LARGE')}:
                    <strong>
                      {coinLocker.storage.large_locker_storage} /{' '}
                      {coinLocker.price?.large_locker_price}
                    </strong>
                  </span>
                )}
                {coinLocker.storage?.middle_locker_storage && (
                  <span className={styles.coinLockerCardPriceSizeNumberPriceItem}>
                    {t('COMMON.COIN_LOCKER.MEDIUM')}:
                    <strong>
                      {coinLocker.storage.middle_locker_storage} /{' '}
                      {coinLocker.price?.middle_locker_price}
                    </strong>
                  </span>
                )}
                {coinLocker.storage?.small_locker_storage && (
                  <span className={styles.coinLockerCardPriceSizeNumberPriceItem}>
                    {t('COMMON.COIN_LOCKER.SMALL')}:
                    <strong>
                      {coinLocker.storage.small_locker_storage} /{' '}
                      {coinLocker.price?.small_locker_price}
                    </strong>
                  </span>
                )}
              </div>
            </dd>
          </dl>,
          styles.coinLockerCardPriceSizeLink
        )}
      </div>
      <div className={styles.coinLockerCardPayment}>
        {wrapWithLink(
          <dl className={styles.coinLockerCardPaymentBox}>
            <dt className={styles.coinLockerCardPaymentTitle}>{t('COMMON.METHOD_OF_PAYMENT')}</dt>
            <dd className={styles.coinLockerCardPaymentDescription}>{coinLocker.payment}</dd>
          </dl>,
          styles.coinLockerCardPaymentLink,
          { target: '_blank' }
        )}
      </div>
      {lockerPlaceUrl && (
        <p className={styles.coinLockerCardReserve}>
          <Link href={lockerPlaceUrl}>
            <a className={styles.coinLockerCardReserveButtonLink} target="_blank">
              <span className={styles.coinLockerCardReserveButtonOld}>
                {t('COMMON.COIN_LOCKER.VIEW_LOCATION_THIS')}
              </span>
            </a>
          </Link>
        </p>
      )}
    </div>
  )
}

export default RecommendedCoinLockerCardOld
